import { CodProofPicker, useProofPicker } from './CodProofPicker';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { Hint } from '@/design-system/hint';
import { Input, InputAffix, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/select';
import { Textarea } from '@/design-system/textarea';
import { toast } from '@/design-system/toaster';
import {
  COD_DEPOSIT_METHODS,
  COD_DEPOSIT_METHOD_LABELS,
  COD_SETTLEMENT_SOURCES,
  COD_SETTLEMENT_SOURCE_HINTS,
  COD_SETTLEMENT_SOURCE_LABELS,
  confirmCodLeg1,
  confirmCodLeg2,
  correctCodConfirmation,
  deliveryDetailUrl,
  type CodDepositMethod,
  type CodLegType,
  type CodOrderDetail,
  type CodSettlementSource,
} from '@/services/api/codOrders';
import { formatRupiah } from './CodBadges';

export type ConfirmCodMode =
  | { kind: 'confirm-leg1' }
  | { kind: 'confirm-leg2' }
  | { kind: 'correct'; legType: CodLegType };

/**
 * Digits only, kept as a decimal STRING.
 *
 * Never `parseFloat`, and never a JS number on this path: `parseFloat`
 * ('2.220.700') is 2.22 — a plausible figure, not an error — so a
 * thousands-separated paste would submit silently wrong. The user may type
 * separators; we strip them and send only what the server's
 * `\d+(\.\d{1,2})?` gate accepts.
 */
const toAmountString = (raw: string): string => raw.replace(/[^\d]/g, '');

/** Group digits for display without touching the value we submit. */
const formatAmountInput = (raw: string): string => {
  const digits = toAmountString(raw);
  return digits ? Number(digits).toLocaleString('id-ID') : '';
};

/** Signed Rupiah with its direction spelled out, for the live delta readout. */
const formatDelta = (value: number): string => {
  if (value === 0) return 'Sesuai — tidak ada selisih';
  const abs = `Rp ${Math.abs(Math.round(value)).toLocaleString('id-ID')}`;
  return value > 0 ? `Lebih ${abs}` : `Kurang ${abs}`;
};

/**
 * The delta plus what it MEANS — a direction alone leaves the operator to work
 * out who owes whom. Leg 1 short is money to collect from the rider; Leg 1 over
 * usually means the PIC fronted the difference. At Leg 2 the same signs point
 * at the PIC instead.
 */
const deltaConsequence = (value: number, isLeg1: boolean): string => {
  if (value === 0) return formatDelta(value);
  const who = isLeg1 ? 'rider' : 'PIC';
  return value < 0
    ? `${formatDelta(value)} dari acuan — harus ditagih ke ${who}`
    : `${formatDelta(value)} dari acuan — mis. termasuk talangan ${isLeg1 ? 'PIC' : 'Finance'}`;
};

/** One cell of the correction breakdown. */
const BreakdownCell: React.FC<{
  label: string;
  value: string;
  hint: string;
  tone?: 'ok' | 'warn';
}> = ({ label, value, hint, tone }) => (
  <div className="flex flex-col gap-0.5 px-4 py-3">
    <span className="text-[11px] uppercase tracking-wider text-text-sub-600">
      {label}
    </span>
    {/* `whitespace-nowrap`: three money cells in one row is exactly where a
        figure gets wrapped or clipped, and half of "Rp 1.248.771" reads as a
        different amount rather than as a rendering problem. The modal is sized
        to fit them instead. */}
    <span
      className={`whitespace-nowrap text-base font-medium tabular-nums ${
        tone === 'warn' ? 'text-state-warning-dark' : 'text-text-strong-950'
      }`}
    >
      {value}
    </span>
    <span className="text-[11px] leading-tight text-text-sub-600">{hint}</span>
  </div>
);

const ConfirmCodModal: React.FC<{
  mode: ConfirmCodMode;
  detail: CodOrderDetail;
  onClose: () => void;
  onDone: () => void;
}> = ({ mode, detail, onClose, onDone }) => {
  const isCorrection = mode.kind === 'correct';
  const legType: CodLegType =
    mode.kind === 'confirm-leg1'
      ? 'rider_to_pic'
      : mode.kind === 'confirm-leg2'
        ? 'pic_to_finance'
        : mode.legType;
  const isLeg1 = legType === 'rider_to_pic';
  const existingLeg = isLeg1 ? detail.legRiderToPic : detail.legPicToFinance;

  const methodOptions = isLeg1
    ? COD_SETTLEMENT_SOURCES.map((value) => ({
        value: value as string,
        label: COD_SETTLEMENT_SOURCE_LABELS[value],
        hint: COD_SETTLEMENT_SOURCE_HINTS[value],
      }))
    : COD_DEPOSIT_METHODS.map((value) => ({
        value: value as string,
        label: COD_DEPOSIT_METHOD_LABELS[value],
        hint: null as string | null,
      }));

  /**
   * What both legs are measured against: the order's ORIGINAL COD amount.
   *
   * Decided by Joshua 2026-09-07 — `detail.amount` is the source of truth for
   * this screen, so both legs state the same reference and the reader never has
   * to ask which number a leg is being judged by.
   *
   * This is a DELIBERATE divergence from the server, which measures Leg 2
   * against Leg 1's `confirmedAmount` (`confirm-cod-leg2.usecase.ts`). The two
   * agree on every order whose rider handed over the full amount, which is the
   * overwhelming majority. They disagree only when Leg 1 came up short, and
   * there the server will record a discrepancy this form calls zero — see the
   * shortfall panel below, which asks who covers the gap and re-seeds the
   * amount accordingly — that is what keeps the operator from submitting a
   * total the server refuses.
   */
  const baseline = detail.amount;
  /**
   * What the SERVER will compare Leg 2 against, kept so the form can warn
   * instead of failing at submit: a Leg 2 whose deposit differs from this needs
   * a `discrepancyNote`, and the server returns 400 without one.
   */
  const serverBaseline = isLeg1
    ? detail.amount
    : (detail.legRiderToPic?.confirmedAmount ?? detail.amount);

  const [method, setMethod] = useState<string>(
    isCorrection && existingLeg?.method
      ? existingLeg.method
      : isLeg1
        ? ''
        : COD_DEPOSIT_METHODS[0]
  );
  const [amountInput, setAmountInput] = useState(
    formatAmountInput(
      String(
        Math.round(
          isCorrection && existingLeg ? existingLeg.confirmedAmount : baseline
        )
      )
    )
  );
  const [discrepancyNote, setDiscrepancyNote] = useState(
    isCorrection ? (existingLeg?.discrepancyNote ?? '') : ''
  );
  const [notes, setNotes] = useState(
    isCorrection ? (existingLeg?.notes ?? '') : ''
  );
  const [reason, setReason] = useState('');
  /**
   * Who absorbs a Leg 1 shortfall. Same three sources Leg 1 itself uses, and
   * the same rule the bulk deposit form applies (§7): asked ONLY when this
   * order's Leg 1 actually closed short, because on a clean order the answer
   * would stamp "somebody covered this" onto money that was never missing —
   * and the server rejects it there.
   */
  const [shortfallCoveredBy, setShortfallCoveredBy] =
    useState<CodSettlementSource>('rider_paid');
  /**
   * The attachment field's state.
   *
   * A correction starts with what the leg already holds, and may prune it: the
   * endpoint stores the list it is given, so removing one is dropping it here
   * and submitting the rest. Every other mode starts empty.
   */
  const picker = useProofPicker(
    isCorrection && existingLeg ? existingLeg.proofFiles : []
  );
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  const amount = toAmountString(amountInput);
  const discrepancy = useMemo(
    () => (amount === '' ? 0 : Number(amount) - baseline),
    [amount, baseline]
  );
  /**
   * The discrepancy the SERVER will record. Usually identical to the one above;
   * different only on a Leg 2 whose Leg 1 was short.
   */
  const serverDiscrepancy = useMemo(
    () => (amount === '' ? 0 : Number(amount) - serverBaseline),
    [amount, serverBaseline]
  );
  // Gated on the server's figure, not the displayed one. A note the server
  // demands and the form never asked for is a 400 the operator cannot read.
  //
  // Except when the picker below has already answered the same question: the
  // server writes the note itself there ("Menutup selisih Leg 1 … ditanggung
  // …"), so asking for it again is a second answer to one question. Computed
  // after `shortfallSource` exists — see below.
  const serverWillDeriveNote = () =>
    needsShortfallSource && shortfallSource !== null;

  /**
   * The RIDER's shortfall at Leg 1 — a different question from whether this
   * deposit matches, and the one that decides whether the picker appears.
   *
   * Only meaningful on a Leg 2 confirmation: at Leg 1 there is no earlier leg
   * to have come up short, and a correction edits a leg that already recorded
   * its own answer.
   */
  const leg1Gap = isLeg1 || isCorrection ? 0 : serverBaseline - detail.amount;
  const needsShortfallSource = leg1Gap !== 0;
  const shortfallSource = needsShortfallSource ? shortfallCoveredBy : null;
  /**
   * What a deposit has to be for this order to read as settled, given the
   * choice above. The whole §7 rule in one line: a write-off banks what the PIC
   * actually holds and the order stays short on purpose; anything else makes
   * the order whole, so the PIC banks the original amount.
   */
  const requiredAmount =
    shortfallSource === 'tribe_write_off' ? serverBaseline : detail.amount;
  /**
   * Whether the typed amount is exactly what the chosen source implies.
   *
   * When it is, the gap against the order amount is INTENTIONAL and the panel
   * below already explains it — so the generic "harus ditagih ke PIC" warning
   * must not fire. Live QA caught that: picking a write-off left the form
   * demanding the PIC be billed for money the tribe had just agreed to absorb.
   */
  const matchesRequired =
    needsShortfallSource && amount !== '' && Number(amount) === requiredAmount;
  const needsNote = serverDiscrepancy !== 0 && !serverWillDeriveNote();
  /**
   * Leg 2 cannot be confirmed without evidence; Leg 1 can.
   *
   * A correction is exempt: it edits a leg that already recorded whatever
   * evidence it had, and leaving the field blank there means "keep what is
   * attached" — demanding a re-upload to fix a typo in an amount would be a
   * different, worse rule.
   */
  const proofRequired = !isLeg1 && !isCorrection;

  /**
   * Follow the picker.
   *
   * The choice CHANGES the figure that settles this order, so leaving the field
   * on the previous source's number would make the form contradict the hint
   * right under the picker. Bulk avoids this by deriving the total outright;
   * here the field stays editable, and this only re-seeds it when the source
   * changes — a number the operator typed afterwards is left alone.
   */
  useEffect(() => {
    if (!needsShortfallSource) return;
    setAmountInput(formatAmountInput(String(Math.round(requiredAmount))));
    // Deliberately keyed on the source alone: re-running on `requiredAmount`
    // would fight the operator's own edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortfallCoveredBy, needsShortfallSource]);

  // Mirrors the server's gate. The server re-checks all of it — a disabled
  // button is not a guarantee — but failing here saves a round trip.
  const errors: Record<string, string> = {};
  if (!method) errors.method = 'Pilih dulu.';
  if (!amount) errors.amount = 'Isi jumlahnya.';
  if (needsNote && !discrepancyNote.trim()) {
    errors.discrepancyNote = 'Wajib diisi karena jumlahnya tidak sama.';
  }
  if (isCorrection && !reason.trim()) {
    errors.reason = 'Wajib diisi.';
  }
  if (proofRequired && picker.isEmpty) {
    errors.proofFile = 'Wajib dilampirkan untuk Leg 2.';
  }
  /**
   * The middle and right cells of the reference breakdown.
   *
   * Built here rather than inline so the three cases stay readable side by
   * side, and so `delta` is computed from the value actually printed beside it.
   *
   * `delta === null` means there is nothing to compare yet — an empty amount
   * field on a first confirmation.
   */
  const middle = useMemo(() => {
    if (isCorrection && existingLeg) {
      return {
        label: 'Terinput sebelumnya',
        value: formatRupiah(existingLeg.confirmedAmount),
        hint: 'Nilai yang akan ditimpa',
        delta: existingLeg.confirmedAmount - baseline,
        deltaLabel: 'Selisih saat ini',
        deltaHint: 'Terinput − acuan',
      };
    }
    if (!isLeg1) {
      // Leg 2. `serverBaseline` IS what Leg 1 closed at, so this cell doubles
      // as the explanation for the "wajib dijelaskan" hint under the amount:
      // the gap shown here is the discrepancy the server will record if the
      // PIC deposits the full order amount.
      return {
        label: 'Diserahkan rider',
        value: formatRupiah(serverBaseline),
        hint: 'Dikonfirmasi di Leg 1',
        delta: serverBaseline - baseline,
        deltaLabel: 'Selisih Leg 1',
        deltaHint: 'Diserahkan − acuan',
      };
    }
    return {
      label: 'Diinput sekarang',
      value: amount === '' ? '—' : formatRupiah(Number(amount)),
      hint: 'Diterima dari rider',
      delta: amount === '' ? null : discrepancy,
      deltaLabel: 'Selisih saat ini',
      deltaHint: 'Terinput − acuan',
    };
  }, [
    isCorrection,
    existingLeg,
    isLeg1,
    baseline,
    serverBaseline,
    amount,
    discrepancy,
  ]);

  const canSubmit =
    Object.keys(errors).length === 0 && !submitting && !picker.uploading;
  const show = (key: string) => (touched ? errors[key] : undefined);

  const title = isCorrection
    ? `Koreksi ${isLeg1 ? 'Leg 1 · Rider → PIC' : 'Leg 2 · PIC → Finance'}`
    : isLeg1
      ? 'Confirm COD · Rider → PIC'
      : 'Confirm Deposit · PIC → Finance';

  const submit = async () => {
    setTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Upload first, then send the urls. Nothing is written if an upload
      // fails, and a failed confirm cannot strand a half-attached list.
      //
      // On a correction this is the list the leg should END UP WITH, so an
      // attachment the operator removed is simply absent from it. `undefined`
      // there would mean "leave the stored list alone", which is why the
      // correction branch below sends the array even when it is empty.
      const proofFiles = await picker.upload();

      if (isCorrection) {
        await correctCodConfirmation(detail.id, legType, {
          method,
          confirmedAmount: amount,
          discrepancyNote: discrepancyNote.trim() || null,
          notes: notes.trim() || null,
          reason: reason.trim(),
          proofFiles,
        });
        toast.success('Koreksi tersimpan.');
      } else if (isLeg1) {
        const result = await confirmCodLeg1(detail.id, {
          settlementSource: method as CodSettlementSource,
          confirmedAmount: amount,
          discrepancyNote: discrepancyNote.trim() || null,
          notes: notes.trim() || null,
          proofFiles,
        });
        // Surfaced rather than hidden: while the sync flag is off, the rider's
        // aggregate in delivery-service still shows this money as outstanding.
        toast.success(
          result.syncedToDeliveryService
            ? 'Leg 1 dikonfirmasi dan tersinkron ke delivery-service.'
            : 'Leg 1 dikonfirmasi. Belum tersinkron ke delivery-service.'
        );
      } else {
        await confirmCodLeg2(detail.id, {
          depositMethod: method as CodDepositMethod,
          depositedAmount: amount,
          discrepancyNote: discrepancyNote.trim() || null,
          notes: notes.trim() || null,
          proofFiles,
          // Null, not a default, when Leg 1 closed clean — the server refuses
          // a source there.
          shortfallCoveredBy: shortfallSource,
        });
        toast.success('Leg 2 dikonfirmasi. Pesanan COD selesai.');
      }
      onDone();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedHint = methodOptions.find((o) => o.value === method)?.hint;

  return (
    <FormModal
      open
      onClose={onClose}
      title={title}
      // The client alone. The two identifiers moved into the body, where both
      // fit in full — repeating the provider reference here as well just made
      // the same string appear twice, once abbreviated by the header's own
      // width and once not.
      subtitle={detail.clientName ?? '—'}
      // `2xl` (672px), up from `lg` (512px). At 512 the correction breakdown's
      // three money cells had ~150px each and the identity line had to be
      // truncated — on a form that writes money, where both the amount and the
      // order it belongs to have to be readable in full.
      size="2xl"
      confirmOnClose
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-text-sub-600">
            Dikonfirmasi sebagai {detail.owner ?? 'user aktif'}
          </span>
          <div className="flex gap-2">
            <Button
              style="stroke"
              tone="neutral"
              onClick={onClose}
              disabled={submitting}
            >
              Batal
            </Button>
            <Button onClick={submit} disabled={submitting}>
              {picker.uploading
                ? 'Mengunggah bukti…'
                : submitting
                  ? 'Menyimpan…'
                  : isCorrection
                    ? 'Simpan'
                    : 'Konfirmasi'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {/* WHICH order this is — both identifiers, neither abbreviated.
            The header subtitle carries the provider reference alone; the
            delivery uid is what the delivery page and support are keyed by, so
            an operator checking a physical package against this form needs
            both, and needs them whole. */}
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
          <span className="break-words font-medium text-text-strong-950">
            {detail.bookingId}
          </span>
          <span className="text-text-sub-600">·</span>
          {detail.deliveryUid ? (
            <a
              href={deliveryDetailUrl(detail.deliveryUid) ?? undefined}
              target="_blank"
              rel="noreferrer"
              className="break-words font-mono text-xs text-accent hover:text-accent-dark hover:underline"
            >
              {detail.deliveryUid}
            </a>
          ) : (
            <span className="font-mono text-xs text-text-sub-600">
              Belum ada delivery
            </span>
          )}
          {detail.hubName && (
            <>
              <span className="text-text-sub-600">·</span>
              <span className="text-xs text-text-sub-600">
                {detail.hubName}
              </span>
            </>
          )}
        </div>

        {/* The reference figures, stated up front. Every field below is judged
            against them, and the baseline differs per leg — leaving the reader
            to infer it is how a PIC ends up blamed for a rider's shortfall.

            A CORRECTION gets three cells, not one. Editing a confirmed figure
            needs all three to be answerable without arithmetic: what it should
            have been, what was actually entered, and how far apart those are
            right now. With only the baseline on screen the operator had to
            remember the stored value they were about to overwrite. */}
        <div className="grid grid-cols-3 divide-x divide-stroke-soft-200 overflow-hidden rounded-lg bg-bg-weak-50">
          <BreakdownCell
            label="Acuan sistem"
            value={formatRupiah(baseline)}
            // One reference for both legs now, so one hint. It used to read
            // "Diserahkan rider di Leg 1" here, which is the cell to the right.
            hint="Nominal COD pesanan"
          />
          {/* The middle cell is whatever the operator needs to compare against
              the acuan, and that is a different fact in each of the three
              cases:

                correction   the stored figure about to be overwritten
                Leg 2        what the rider actually handed over at Leg 1
                Leg 1        the amount being typed, the only value that exists

              It used to echo the live input on Leg 2 as well, which told the
              reader something they could already see in the field below. What
              they cannot see there is whether the rider came up short — which
              is exactly the number that decides whether this deposit will
              close the order or leave a gap. */}
          <BreakdownCell
            label={middle.label}
            value={middle.value}
            hint={middle.hint}
          />
          {/* Derived from the acuan shown two cells to the left, never from the
              stored `discrepancyAmount`. The stored figure is measured against
              the server's own baseline, so on a Leg 2 with a short Leg 1 it
              would print a number that does not equal middle − acuan and make
              the row look like an arithmetic bug. */}
          <BreakdownCell
            label={middle.deltaLabel}
            value={middle.delta === null ? '—' : formatDelta(middle.delta)}
            hint={middle.deltaHint}
            tone={middle.delta === 0 || middle.delta === null ? 'ok' : 'warn'}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cod-method">
              {isLeg1 ? 'Settlement Source' : 'Metode Deposit'}
            </Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger id="cod-method">
                <SelectValue placeholder="Pilih…" />
              </SelectTrigger>
              <SelectContent>
                {methodOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {show('method') ? (
              <Hint tone="error">{show('method')}</Hint>
            ) : (
              selectedHint && <Hint>{selectedHint}</Hint>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cod-amount">
              {isLeg1 ? 'Jumlah Diterima dari Rider' : 'Jumlah Disetor'}
            </Label>
            {/* InputRoot is what draws the box. Without it the value renders
                as bare text and nobody can tell the amount is editable — which
                is the single most important field on this form. */}
            <InputRoot size="md" invalid={!!show('amount')}>
              {/* `Rp` as a prefix rather than inside the value: the field is
                  parsed as digits, so a currency mark typed into it would have
                  to be stripped again on every keystroke. GSM's InputAffix
                  puts it inside the box, so the number still reads as money
                  without the input having to carry it.

                  `text-text-sub-600`, not the affix default `soft-400`: the
                  default is placeholder-grey and read as disabled next to a
                  filled amount. */}
              <InputAffix className="text-text-sub-600">Rp</InputAffix>
              <Input
                id="cod-amount"
                value={amountInput}
                inputMode="numeric"
                placeholder="0"
                className="tabular-nums"
                onChange={(event) =>
                  setAmountInput(formatAmountInput(event.target.value))
                }
              />
            </InputRoot>
            {show('amount') ? (
              <Hint tone="error">{show('amount')}</Hint>
            ) : (
              // No special case for "matches the order but Leg 1 was short":
              // that is exactly when the shortfall panel below is on screen,
              // and it explains the same gap better — with the figure, the
              // choice that caused it, and what the deposit becomes. An earlier
              // version warned here too, and live QA caught it telling the
              // operator the difference was "wajib dijelaskan" while the form
              // showed no note field at all, because the server derives that
              // note once a source is picked.
              <Hint
                tone={
                  discrepancy === 0 || matchesRequired ? 'neutral' : 'warning'
                }
              >
                {matchesRequired
                  ? 'Sesuai penanggung selisih yang dipilih di bawah'
                  : deltaConsequence(discrepancy, isLeg1)}
              </Hint>
            )}
          </div>
        </div>

        {needsShortfallSource && (
          <div className="flex flex-col gap-3 rounded-lg border border-state-warning-light bg-state-warning-lighter px-4 py-3">
            <span className="text-sm text-text-strong-950">
              <strong>
                Rider kurang menyerahkan {formatRupiah(Math.abs(leg1Gap))} di
                Leg 1.
              </strong>{' '}
              Selisih itu terjadi sebelum tahap ini — pilih dulu siapa yang
              menanggungnya, karena itu yang menentukan berapa yang harus
              disetor sekarang.
            </span>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cod-shortfall-source">
                Siapa yang menanggung selisih Leg 1?
              </Label>
              <Select
                value={shortfallCoveredBy}
                onValueChange={(value) =>
                  setShortfallCoveredBy(value as CodSettlementSource)
                }
              >
                <SelectTrigger
                  id="cod-shortfall-source"
                  className="bg-bg-white-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COD_SETTLEMENT_SOURCES.map((source) => (
                    <SelectItem key={source} value={source}>
                      {COD_SETTLEMENT_SOURCE_LABELS[source]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* The consequence spelled out, because the choice changes the
                  required figure — and a write-off deliberately leaves the
                  order short, the one outcome nobody should pick by accident.
                  Same wording as the bulk form, on purpose: two screens
                  describing one rule differently is how the rule gets
                  misremembered. */}
              <Hint>
                {shortfallCoveredBy === 'tribe_write_off'
                  ? `Selisih ditanggung perusahaan — PIC hanya menyetor yang benar-benar dipegang (${formatRupiah(
                      requiredAmount
                    )}), dan pesanan tetap tercatat kurang.`
                  : `Selisih ditutup, jadi PIC menyetor nominal asli pesanan (${formatRupiah(
                      requiredAmount
                    )}) dan pesanan tercatat utuh.`}
              </Hint>
            </div>
          </div>
        )}

        {needsNote && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cod-discrepancy-note">Catatan Selisih</Label>
            <Textarea
              id="cod-discrepancy-note"
              value={discrepancyNote}
              onChange={(event) => setDiscrepancyNote(event.target.value)}
              placeholder="Kenapa jumlahnya berbeda? Ini yang dibaca Finance."
              rows={2}
            />
            {show('discrepancyNote') && (
              <Hint tone="error">{show('discrepancyNote')}</Hint>
            )}
          </div>
        )}

        {isCorrection && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cod-reason">Alasan Koreksi</Label>
            <Textarea
              id="cod-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Apa yang membuat angka sebelumnya salah?"
              rows={2}
            />
            {show('reason') ? (
              <Hint tone="error">{show('reason')}</Hint>
            ) : (
              <Hint>
                Nilai lama otomatis tersimpan di riwayat. Alasan ini yang
                menjelaskan kenapa berubah.
              </Hint>
            )}
          </div>
        )}

        <CodProofPicker
          picker={picker}
          required={proofRequired}
          // Only a correction may prune the stored list; every other mode is
          // adding to a leg that is being created right now.
          allowRemoveExisting={isCorrection}
          hint={
            show('proofFile') ? (
              <Hint tone="error">{show('proofFile')}</Hint>
            ) : (
              <Hint>
                {isCorrection
                  ? 'Hapus yang tidak sesuai, tambah yang baru. Yang tersisa di sini yang akan tersimpan, dan daftar lamanya tetap tercatat di riwayat.'
                  : proofRequired
                    ? 'Wajib untuk Leg 2 — Finance mencocokkan setoran ini dengan mutasi rekening.'
                    : 'Boleh dikosongkan. Baris tanpa bukti ditandai di list supaya Finance bisa membedakannya.'}
              </Hint>
            )
          }
        />

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cod-notes">Catatan</Label>
          <Textarea
            id="cod-notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Opsional"
            rows={2}
          />
        </div>
      </div>
    </FormModal>
  );
};

export default ConfirmCodModal;
